import { useEffect } from 'react'

import { Code } from '@connectrpc/connect'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { useTranslation } from 'react-i18next'

import { Alert } from '@enechain/ecloud-designsystem'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { RegionLoading } from '~/components/common/RegionLoading'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { todayCivilDate } from '~/models/civilDate'
import { BgMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/BgMaster/components/BgMasterFormFields'
import { useGetBgMaster } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useGetBgMaster'
import type { BgMasterRow } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { useUpdateBgMaster } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useUpdateBgMaster'
import {
  BgMasterEditFormSchema,
  type BgMasterFormInput,
  type BgMasterFormValues,
} from '~/pages/AdminOcctoMaster/tabs/BgMaster/models/schema'
import { resolveBgMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/BgMaster/models/serverError'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'

type Props = {
  isOpen: boolean
  row: BgMasterRow
  onClose: () => void
}

const BG_MASTER_EDIT_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.Aborted]: 'page:adminOcctoMaster.bgMaster.serverError.aborted',
  [Code.NotFound]: 'page:adminOcctoMaster.bgMaster.serverError.notFound',
  [Code.InvalidArgument]:
    'page:adminOcctoMaster.bgMaster.serverError.invalidArgument',
}

export const EditBgMasterModal = ({
  isOpen,
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdateBgMaster()
  const { data, isPending: isFetching, isError, error } = useGetBgMaster(row.id)
  const fetched = data?.bgMaster

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    reset,
    setValue,
    watch,
    isDirty,
  } = useCrudModalForm<BgMasterFormInput, BgMasterFormValues>({
    // Edit は bgCode / areaCode とも readonly のため AC-BG14 (不一致検証) を
    // 適用しない schema を使う (= 本 feature 追加前の不整合レコードが他
    // フィールド編集の保存でロックアウトされるのを防ぐ。schema.ts の
    // buildBgMasterFormSchema JSDoc 参照)。
    schema: BgMasterEditFormSchema,
    defaultValues: {
      bgCode: '',
      bgName: '',
      licenseType: row.licenseType as BgMasterFormInput['licenseType'],
      areaCode: row.areaCode,
      powerType: row.powerType,
      repBusinessMasterId: '',
      members: [
        {
          businessMasterId: '',
          isOwn: false,
          validFrom: todayCivilDate(),
          validTo: undefined,
        },
      ],
      plants: [],
      wheelingLinkCode: undefined,
      validFrom: todayCivilDate(),
      validTo: undefined,
      note: '',
    },
    onSubmit: async (values) => {
      if (fetched?.updatedAt === undefined) {
        throw new Error(t('page:adminOcctoMaster.bgMaster.serverError.unknown'))
      }
      const isGeneration = values.licenseType === LicenseType.GENERATION
      await mutateAsync({
        id: row.id,
        bgName: values.bgName,
        powerType: isGeneration ? values.powerType : undefined,
        repBusinessMasterId: values.repBusinessMasterId,
        members: values.members.map((m) => ({
          businessMasterId: m.businessMasterId,
          isOwn: m.isOwn,
          validFrom: m.validFrom,
          validTo: m.validTo,
        })),
        // 配下発電所は発電BGのみ有効 (AC-05/AC-06)。 非発電では
        // BgPlantMembershipSection が自動でクリアするが、 send 時も defensive
        // に空配列へ倒す。
        plants: isGeneration
          ? values.plants.map((p) => ({
              plantMasterId: p.plantMasterId,
              validFrom: p.validFrom,
              validTo: p.validTo,
            }))
          : [],
        validFrom: values.validFrom,
        validTo: values.validTo,
        contractIdNo1:
          isGeneration &&
          'contractIdNo1' in values &&
          values.contractIdNo1 !== undefined &&
          values.contractIdNo1 !== ''
            ? values.contractIdNo1
            : undefined,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        wheelingLinkCode: values.wheelingLinkCode,
        expectedUpdatedAt: fetched.updatedAt,
      })
    },
    onClose,
    customErrorKeys: BG_MASTER_EDIT_ERROR_KEYS,
    unknownErrorKey: 'page:adminOcctoMaster.bgMaster.serverError.unknown',
    resolveDetailedError: resolveBgMasterDetailedError,
  })

  useEffect(() => {
    if (fetched !== undefined) {
      // AC-03: 保存済みの ValidFrom / ValidTo を表示する。 validFrom は proto 上
      // 必須だが TS 型は message field の慣習で optional (= 欠落は起きない想定、
      // 防御的に todayCivilDate() へフォールバック)。
      const orderedMembers = fetched.members.map((m) => ({
        businessMasterId: m.businessMasterId,
        isOwn: m.isOwn,
        validFrom:
          m.validFrom !== undefined
            ? {
                year: m.validFrom.year,
                month: m.validFrom.month,
                day: m.validFrom.day,
              }
            : todayCivilDate(),
        validTo:
          m.validTo !== undefined
            ? {
                year: m.validTo.year,
                month: m.validTo.month,
                day: m.validTo.day,
              }
            : undefined,
      }))
      const repIndex = orderedMembers.findIndex(
        (m) => m.businessMasterId === fetched.repBusinessMasterId,
      )
      if (repIndex > 0) {
        const [rep] = orderedMembers.splice(repIndex, 1)
        orderedMembers.unshift(rep)
      }
      // AC-12: 保存済みの配下発電所を有効期間付きで表示する。 validFrom は
      // proto 上必須だが TS 型は message field の慣習で optional (= members と
      // 同様、 防御的に todayCivilDate() へフォールバック)。
      const orderedPlants = fetched.plants.map((p) => ({
        plantMasterId: p.plantMasterId,
        validFrom:
          p.validFrom !== undefined
            ? {
                year: p.validFrom.year,
                month: p.validFrom.month,
                day: p.validFrom.day,
              }
            : todayCivilDate(),
        validTo:
          p.validTo !== undefined
            ? {
                year: p.validTo.year,
                month: p.validTo.month,
                day: p.validTo.day,
              }
            : undefined,
      }))
      reset({
        bgCode: fetched.bgCode,
        bgName: fetched.bgName,
        licenseType: fetched.licenseType,
        areaCode: fetched.areaCode,
        powerType: fetched.powerType,
        repBusinessMasterId: fetched.repBusinessMasterId,
        members: orderedMembers,
        plants: orderedPlants,
        // AC-05: 保存済みの ValidFrom (適用開始日、編集可能) / ValidTo を表示する。
        validFrom:
          fetched.validFrom !== undefined
            ? {
                year: fetched.validFrom.year,
                month: fetched.validFrom.month,
                day: fetched.validFrom.day,
              }
            : todayCivilDate(),
        validTo:
          fetched.validTo !== undefined
            ? {
                year: fetched.validTo.year,
                month: fetched.validTo.month,
                day: fetched.validTo.day,
              }
            : undefined,
        contractIdNo1: fetched.contractIdNo1 ?? '',
        wheelingLinkCode: fetched.wheelingLinkCode,
        note: fetched.note ?? '',
      } as BgMasterFormInput)
    }
  }, [fetched, reset])

  return (
    <CrudModalLayout
      handleSave={handleSave}
      // 入力途中で閉じようとした時に離脱確認ダイアログを出す。 取得値の反映は
      // reset() で defaultValues ごと差し替わるため dirty にならない。
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      isSaveDisabled={isFetching || isError}
      onClose={handleClose}
      serverError={serverError}
      // design (`design/admin/occto.html` #occto-bg-modal) は width:min(740px,94vw)。
      // CreateBgMasterModal と揃える (詳細は同ファイルのコメント参照)。
      size="lg"
      title={t('page:adminOcctoMaster.bgMaster.modal.editTitle')}
    >
      {isError && (
        <Alert
          status="error"
          title={mapMasterGetError(
            t,
            error,
            'page:adminOcctoMaster.bgMaster.serverError',
          )}
        />
      )}
      <RegionLoading isLoading={isFetching}>
        {isFetching ? null : (
          <BgMasterFormFields
            control={control}
            errors={errors}
            isEdit
            isOpen={isOpen}
            setValue={setValue}
            watch={watch}
          />
        )}
      </RegionLoading>
    </CrudModalLayout>
  )
}
